import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import './AdminConsolePage.css';

interface User {
  email: string;
  name: string;
  role: string;
  is_approved: boolean;
  is_active: boolean;
  created_at: string;
  approved_at?: string;
  approved_by?: string;
}

const AVAILABLE_ROLES = ['Admin', 'QA_Engineer', 'Viewer'];

export const AdminConsolePage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'add_user' | 'all' | 'roles'>('add_user');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('Viewer');

  // Add User form state
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('Viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab === 'all') loadUsers();
  }, [activeTab]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const users = await apiClient.getAllUsers();
      setAllUsers(users);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      await apiClient.createUser({ name: newUserName, email: newUserEmail, role: newUserRole });
      setSuccessMessage(`User "${newUserName}" added successfully with role "${newUserRole}". They can now log in using "Continue with Google" with their ${newUserEmail} account.`);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserRole('Viewer');
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async (userEmail: string) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      setError(''); setSuccessMessage('');
      await apiClient.deactivateUser(userEmail);
      setSuccessMessage('User deactivated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (userEmail: string) => {
    try {
      setError(''); setSuccessMessage('');
      await apiClient.activateUser(userEmail);
      setSuccessMessage('User activated successfully');
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to activate user');
    }
  };

  const handleChangeRole = async (userEmail: string, newRole: string) => {
    try {
      setError(''); setSuccessMessage('');
      await apiClient.updateUserRole(userEmail, newRole);
      setSuccessMessage('Role updated successfully');
      setEditingRole(null);
      loadUsers();
    } catch (err: any) {
      setError(err.message || 'Failed to update role');
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  if (currentUser?.role !== 'Admin') {
    return (
      <div className="admin-console-page">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access the admin console.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-console-page">
      <div className="admin-header">
        <h1>Admin Console</h1>
        <p>Manage user accounts and permissions</p>
      </div>

      <div className="admin-tabs">
        <button className={`tab ${activeTab === 'add_user' ? 'active' : ''}`} onClick={() => setActiveTab('add_user')}>
          Add User
        </button>
        <button className={`tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          All Users ({allUsers.length})
        </button>
        <button className={`tab ${activeTab === 'roles' ? 'active' : ''}`} onClick={() => setActiveTab('roles')}>
          Role Management
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {isLoading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="users-container">

          {/* ── Add User Tab ── */}
          {activeTab === 'add_user' && (
            <div className="add-user-section">
              <h3>Add New User</h3>
              <p className="add-user-hint">Enter the user's details below. They will be able to log in using <strong>Continue with Google</strong> with the email address you provide — no password needed.</p>
              <form className="add-user-form" onSubmit={handleAddUser}>
                <div className="form-group">
                  <label htmlFor="new-user-name">Full Name <span className="required">*</span></label>
                  <input
                    id="new-user-name"
                    type="text"
                    className="form-control"
                    placeholder="e.g. John Doe"
                    value={newUserName}
                    onChange={e => setNewUserName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-user-email">Email Address <span className="required">*</span></label>
                  <input
                    id="new-user-email"
                    type="email"
                    className="form-control"
                    placeholder="e.g. john@company.com"
                    value={newUserEmail}
                    onChange={e => setNewUserEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-user-role">Role <span className="required">*</span></label>
                  <select
                    id="new-user-role"
                    className="form-control"
                    value={newUserRole}
                    onChange={e => setNewUserRole(e.target.value)}
                  >
                    {AVAILABLE_ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <p className="role-hint">
                    {newUserRole === 'Admin' && 'Full system access including user management.'}
                    {newUserRole === 'QA_Engineer' && 'Can create, edit, and execute tests.'}
                    {newUserRole === 'Viewer' && 'Read-only access to view tests and results.'}
                  </p>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── All Users Tab ── */}
          {activeTab === 'all' && (
            <div className="all-users">
              {allUsers.length === 0 ? (
                <div className="empty-state"><p>No users found</p></div>
              ) : (
                <div className="users-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allUsers.map((user) => (
                        <tr key={user.email}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            {editingRole === user.email ? (
                              <div className="role-edit">
                                <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="role-select">
                                  {AVAILABLE_ROLES.map(role => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                                <button className="btn btn-sm btn-success" onClick={() => handleChangeRole(user.email, selectedRole)}>Save</button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingRole(null)}>Cancel</button>
                              </div>
                            ) : (
                              <div className="role-display">
                                <span className={`role-badge ${user.role}`}>{user.role}</span>
                                {user.email !== currentUser?.email && (
                                  <button className="btn-icon" onClick={() => { setEditingRole(user.email); setSelectedRole(user.role); }} title="Change role">✏️</button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                            {!user.is_approved && <span className="status-badge pending">Pending</span>}
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>
                            {user.email !== currentUser?.email && (
                              <div className="action-buttons">
                                {user.is_active ? (
                                  <button className="btn btn-sm btn-warning" onClick={() => handleDeactivate(user.email)}>Deactivate</button>
                                ) : (
                                  <button className="btn btn-sm btn-success" onClick={() => handleActivate(user.email)}>Activate</button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Role Management Tab ── */}
          {activeTab === 'roles' && (
            <div className="role-management">
              <div className="roles-info">
                <h3>Available Roles</h3>
                <div className="roles-grid">
                  {AVAILABLE_ROLES.map(role => (
                    <div key={role} className="role-card">
                      <h4>{role}</h4>
                      <p className="role-description">
                        {role === 'Admin' && 'Full system access including user management'}
                        {role === 'QA_Engineer' && 'Can create and execute tests'}
                        {role === 'Viewer' && 'Read-only access to view tests and results'}
                      </p>
                      <div className="role-permissions">
                        <strong>Permissions:</strong>
                        <ul>
                          {role === 'Admin' && (<><li>✓ Manage users and roles</li><li>✓ Create/edit/delete projects</li><li>✓ Create/edit/delete scenarios</li><li>✓ Create/edit/delete test cases</li><li>✓ Execute tests</li><li>✓ Export and import</li></>)}
                          {role === 'QA_Engineer' && (<><li>✓ Create/edit/delete scenarios</li><li>✓ Create/edit/delete test cases</li><li>✓ Execute tests</li><li>✓ Export and import</li><li>✓ View projects</li></>)}
                          {role === 'Viewer' && (<><li>✓ View projects</li><li>✓ View scenarios</li><li>✓ View test cases</li><li>✓ View test results</li></>)}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

